import React, { createContext, useState, useRef, useEffect } from "react";
import { io } from "socket.io-client";
import Peer from "simple-peer";

const SocketContext = createContext();
const socket = io("https://meet.catlog.se", { transports: ["websocket", "polling"] });

const ICE_SERVERS = [
  {
    urls: "turns:meet.catlog.se:5349?transport=tcp",
    username: "webrtcuser",
    credential: "wQR^~BGGaQ1^Pj9gYAp",
  },
  {
    urls: "turn:meet.catlog.se:5349?transport=udp",
    username: "webrtcuser",
    credential: "wQR^~BGGaQ1^Pj9gYAp",
  },
  {
    urls: "turn:meet.catlog.se:3478?transport=udp",
    username: "webrtcuser",
    credential: "wQR^~BGGaQ1^Pj9gYAp",
  },
  {
    urls: "turn:meet.catlog.se:3478?transport=tcp",
    username: "webrtcuser",
    credential: "wQR^~BGGaQ1^Pj9gYAp",
  },
];

const CHAT_TTL_MS = 5 * 60 * 1000;

const params = new URLSearchParams(window.location.search);
const roomToken = params.get("token") || "";
const roomSecret = params.get("secret") || "";
const SECRET = "ca93F3v3G2UsQG3Q1nGm";
const validRoomAccess = Boolean(roomToken && roomSecret === SECRET);

const ContextProvider = ({ children }) => {
  const [callAccepted, setCallAccepted] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [stream, setStream] = useState();
  const [name, setNameState] = useState(() => localStorage.getItem("displayName") || "");
  const [call, setCall] = useState({});
  const [me, setMe] = useState("");
  const [pendingPeerId, setPendingPeerId] = useState("");
  const [roomMessages, setRoomMessages] = useState([]);

  const [audioInputs, setAudioInputs] = useState([]);
  const [videoInputs, setVideoInputs] = useState([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState("");
  const [selectedVideoInput, setSelectedVideoInput] = useState("");
  const [micLevel, setMicLevel] = useState(0);

  const myVideo = useRef();
  const userVideo = useRef();
  const connectionRef = useRef();
  const autoAnsweredRef = useRef(false);
  const audioCtxRef = useRef(null);
  const meterRafRef = useRef(null);

  const effectiveName = (name || "").trim() || "Deltagare";

  const pruneChatMessages = (messages) => {
    const cutoff = Date.now() - CHAT_TTL_MS;
    return (messages || []).filter((msg) => Number(msg.createdAt) >= cutoff);
  };

  const setName = (value) => {
    setNameState(value);
    localStorage.setItem("displayName", value);
  };

  const stopMeter = () => {
    if (meterRafRef.current) {
      cancelAnimationFrame(meterRafRef.current);
      meterRafRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    setMicLevel(0);
  };

  const startMeter = (mediaStream) => {
    stopMeter();
    const track = mediaStream?.getAudioTracks?.()[0];
    if (!track) return;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    audioCtxRef.current = ctx;

    const tick = () => {
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) sum += data[i];
      const avg = Math.min(100, Math.round(sum / data.length));
      setMicLevel(avg);
      meterRafRef.current = requestAnimationFrame(tick);
    };

    tick();
  };

  const refreshDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const aud = devices.filter((d) => d.kind === "audioinput");
      const vid = devices.filter((d) => d.kind === "videoinput");
      setAudioInputs(aud);
      setVideoInputs(vid);
      if (!selectedAudioInput && aud[0]?.deviceId) setSelectedAudioInput(aud[0].deviceId);
      if (!selectedVideoInput && vid[0]?.deviceId) setSelectedVideoInput(vid[0].deviceId);
    } catch (e) {
      console.error("enumerateDevices failed", e);
    }
  };

  const replacePeerTracks = (oldStream, newStream) => {
    const peer = connectionRef.current;
    if (!peer) return;

    const newAudio = newStream?.getAudioTracks?.()[0] || null;
    const newVideo = newStream?.getVideoTracks?.()[0] || null;

    const pc = peer?._pc;
    const senders = pc?.getSenders?.() || [];

    try {
      const audioSender = senders.find((sender) => sender?.track?.kind === "audio");
      if (audioSender && newAudio) {
        audioSender.replaceTrack(newAudio);
      } else if (peer.replaceTrack) {
        const oldAudio = oldStream?.getAudioTracks?.()[0];
        if (oldAudio && newAudio) peer.replaceTrack(oldAudio, newAudio, oldStream);
      }
    } catch (e) {
      console.error("replace audio track failed", e);
    }

    try {
      const videoSender = senders.find((sender) => sender?.track?.kind === "video");
      if (videoSender && newVideo) {
        videoSender.replaceTrack(newVideo);
      } else if (peer.replaceTrack) {
        const oldVideo = oldStream?.getVideoTracks?.()[0];
        if (oldVideo && newVideo) peer.replaceTrack(oldVideo, newVideo, oldStream);
      }
    } catch (e) {
      console.error("replace video track failed", e);
    }
  };

  const startMedia = async (audioId, videoId) => {
    const constraints = {
      audio: audioId ? { deviceId: { exact: audioId } } : true,
      video: videoId ? { deviceId: { exact: videoId } } : true,
    };

    const newStream = await navigator.mediaDevices.getUserMedia(constraints);
    const oldStream = stream;

    if (myVideo.current) myVideo.current.srcObject = newStream;
    setStream(newStream);
    startMeter(newStream);

    if (oldStream) {
      replacePeerTracks(oldStream, newStream);
      oldStream.getTracks().forEach((t) => t.stop());
    }
  };

  const resetPeerState = () => {
    if (connectionRef.current) {
      try {
        connectionRef.current.destroy();
      } catch (e) {
        console.debug("peer destroy cleanup", e);
      }
      connectionRef.current = null;
    }
    if (userVideo.current) userVideo.current.srcObject = null;
    setCallAccepted(false);
    setCallEnded(false);
    setCall({});
    setPendingPeerId("");
    autoAnsweredRef.current = false;
  };

  useEffect(() => {
    startMedia(selectedAudioInput, selectedVideoInput)
      .then(() => refreshDevices())
      .catch((err) => console.error("getUserMedia failed:", err));

    socket.on("me", (id) => {
      setMe(id);
      socket.emit("setName", { name: effectiveName });
      if (validRoomAccess) socket.emit("joinRoom", { token: roomToken });
    });

    socket.on("roomPeer", ({ peerId, peerName }) => {
      if (peerId) {
        setPendingPeerId(peerId);
        if (peerName) setCall((prev) => ({ ...prev, name: peerName }));
      }
    });

    socket.on("roomPeerLeft", () => {
      resetPeerState();
    });

    socket.on("callEnded", () => {
      resetPeerState();
      if (validRoomAccess) socket.emit("joinRoom", { token: roomToken });
    });

    socket.on("nameUpdated", ({ name: updatedName }) => {
      if (!updatedName) return;
      setCall((prev) => ({ ...prev, name: updatedName }));
    });

    socket.on("callUser", ({ from, name: callerName, signal }) => {
      setCall({ isReceivingCall: true, from, name: callerName, signal });
    });

    socket.on("roomChatHistory", (history) => {
      setRoomMessages(pruneChatMessages(history));
    });

    socket.on("roomChatMessage", (message) => {
      setRoomMessages((prev) => pruneChatMessages([...(prev || []), message]));
    });

    const onDeviceChange = () => refreshDevices();
    navigator.mediaDevices?.addEventListener?.("devicechange", onDeviceChange);

    const pruneTimer = setInterval(() => {
      setRoomMessages((prev) => pruneChatMessages(prev));
    }, 15000);

    return () => {
      socket.off("me");
      socket.off("roomPeer");
      socket.off("roomPeerLeft");
      socket.off("callUser");
      socket.off("callEnded");
      socket.off("callAccepted");
      socket.off("nameUpdated");
      socket.off("roomChatHistory");
      socket.off("roomChatMessage");
      navigator.mediaDevices?.removeEventListener?.("devicechange", onDeviceChange);
      clearInterval(pruneTimer);
      stopMeter();
    };
  }, []);

  useEffect(() => {
    if (!me) return;
    socket.emit("setName", { name: effectiveName });
  }, [name, me, effectiveName]);

  useEffect(() => {
    if (!pendingPeerId || !stream || callAccepted || connectionRef.current) return;
    callUser(pendingPeerId);
    setPendingPeerId("");
  }, [pendingPeerId, stream, callAccepted]);

  useEffect(() => {
    if (!validRoomAccess) return;
    if (!call?.isReceivingCall || !stream || callAccepted || autoAnsweredRef.current) return;
    autoAnsweredRef.current = true;
    answerCall();
  }, [call, stream, callAccepted]);

  const answerCall = () => {
    if (!stream) return;
    setCallAccepted(true);

    const peer = new Peer({
      initiator: false,
      trickle: false,
      stream,
      config: { iceServers: ICE_SERVERS, iceTransportPolicy: "relay" },
    });

    peer.on("signal", (data) => {
      socket.emit("answerCall", { signal: data, to: call.from, name: effectiveName });
    });

    peer.on("stream", (currentStream) => {
      if (userVideo.current) userVideo.current.srcObject = currentStream;
    });

    peer.signal(call.signal);
    connectionRef.current = peer;
  };

  const callUser = (id) => {
    if (!stream) return;

    const peer = new Peer({
      initiator: true,
      trickle: false,
      stream,
      config: { iceServers: ICE_SERVERS, iceTransportPolicy: "relay" },
    });

    peer.on("signal", (data) => {
      socket.emit("callUser", {
        userToCall: id,
        signalData: data,
        from: me,
        name: effectiveName,
      });
    });

    peer.on("stream", (currentStream) => {
      if (userVideo.current) userVideo.current.srcObject = currentStream;
    });

    socket.off("callAccepted");
    socket.on("callAccepted", (payload) => {
      const signal = payload?.signal || payload;
      if (payload?.name) setCall((prev) => ({ ...prev, name: payload.name }));
      setCallAccepted(true);
      peer.signal(signal);
    });

    connectionRef.current = peer;
  };

  const sendRoomMessage = (message) => {
    if (!validRoomAccess) return;
    const cleanMessage = (message || "").trim();
    if (!cleanMessage) return;
    socket.emit("roomChatMessage", { message: cleanMessage });
  };

  const leaveCall = () => {
    resetPeerState();
    window.location.href = "https://catlog.se/boka/";
  };

  const toggleMute = () => {
    if (!stream) return;
    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) audioTrack.enabled = !audioTrack.enabled;
  };

  const toggleVideo = () => {
    if (!stream) return;
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) videoTrack.enabled = !videoTrack.enabled;
  };

  const switchAudioInput = async (deviceId) => {
    setSelectedAudioInput(deviceId);
    try {
      await startMedia(deviceId, selectedVideoInput);
      await refreshDevices();
    } catch (e) {
      console.error("switchAudioInput failed", e);
    }
  };

  const switchVideoInput = async (deviceId) => {
    setSelectedVideoInput(deviceId);
    try {
      await startMedia(selectedAudioInput, deviceId);
      await refreshDevices();
    } catch (e) {
      console.error("switchVideoInput failed", e);
    }
  };

  return (
    <SocketContext.Provider
      value={{
        call,
        callAccepted,
        myVideo,
        userVideo,
        stream,
        name,
        setName,
        callEnded,
        me,
        callUser,
        leaveCall,
        answerCall,
        toggleMute,
        toggleVideo,
        audioInputs,
        videoInputs,
        selectedAudioInput,
        selectedVideoInput,
        switchAudioInput,
        switchVideoInput,
        micLevel,
        roomMessages,
        sendRoomMessage,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export { ContextProvider, SocketContext };
